// Console log and error capture utility
// Captures console logs, errors, and warnings for debugging
class ConsoleCapture {
    constructor(maxEntries = 100) {
        this.logs = [];
        this.errors = [];
        this.warnings = [];
        this.maxEntries = 100;
        this.isCapturing = false;
        // Store original console methods
        this.originalConsole = {
            log: console.log,
            error: console.error,
            warn: console.warn,
            info: console.info,
            debug: console.debug
        };
        /**
         * Handle global errors
         */
        this.handleError = (event) => {
            const errorEntry = {
                message: event.message,
                stack: event.error?.stack,
                timestamp: new Date().toISOString(),
                source: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                filename: event.filename
            };
            this.errors.push(errorEntry);
            this.trimArray(this.errors);
            // Also add to logs
            this.logs.push({
                type: 'error',
                message: event.message,
                timestamp: errorEntry.timestamp,
                stack: errorEntry.stack
            });
            this.trimArray(this.logs);
        };
        /**
         * Handle unhandled promise rejections
         */
        this.handleUnhandledRejection = (event) => {
            const reason = event.reason;
            const message = reason instanceof Error ? reason.message : String(reason);
            const stack = reason instanceof Error ? reason.stack : undefined;
            const errorEntry = {
                message: `Unhandled Promise Rejection: ${message}`,
                stack,
                timestamp: new Date().toISOString()
            };
            this.errors.push(errorEntry);
            this.trimArray(this.errors);
            // Also add to logs
            this.logs.push({
                type: 'error',
                message: errorEntry.message,
                timestamp: errorEntry.timestamp,
                stack: errorEntry.stack
            });
            this.trimArray(this.logs);
        };
        this.maxEntries = maxEntries;
    }
    /**
     * Start capturing console logs and errors
     */
    startCapture() {
        if (this.isCapturing) {
            return;
        }
        this.isCapturing = true;
        // Override console methods
        this.overrideConsoleMethods();
        // Listen for global errors
        this.setupErrorListeners();
    }
    /**
     * Stop capturing and restore original console
     */
    stopCapture() {
        if (!this.isCapturing) {
            return;
        }
        this.isCapturing = false;
        // Restore original console methods
        console.log = this.originalConsole.log;
        console.error = this.originalConsole.error;
        console.warn = this.originalConsole.warn;
        console.info = this.originalConsole.info;
        console.debug = this.originalConsole.debug;
        // Remove error listeners
        window.removeEventListener('error', this.handleError);
        window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
    }
    /**
     * Override console methods to capture logs
     */
    overrideConsoleMethods() {
        const self = this;
        console.log = function (...args) {
            self.captureLog('log', args);
            self.originalConsole.log.apply(console, args);
        };
        console.error = function (...args) {
            self.captureLog('error', args);
            self.originalConsole.error.apply(console, args);
        };
        console.warn = function (...args) {
            self.captureLog('warn', args);
            self.originalConsole.warn.apply(console, args);
        };
        console.info = function (...args) {
            self.captureLog('info', args);
            self.originalConsole.info.apply(console, args);
        };
        console.debug = function (...args) {
            self.captureLog('debug', args);
            self.originalConsole.debug.apply(console, args);
        };
    }
    /**
     * Capture a console log entry
     */
    captureLog(type, args) {
        const entry = {
            type,
            message: this.formatArgs(args),
            timestamp: new Date().toISOString(),
            args: this.serializeArgs(args)
        };
        // Add stack trace for errors
        if (type === 'error') {
            entry.stack = new Error().stack;
        }
        // Store in appropriate array
        if (type === 'error') {
            this.errors.push({
                message: entry.message,
                stack: entry.stack,
                timestamp: entry.timestamp
            });
            this.trimArray(this.errors);
        }
        else if (type === 'warn') {
            this.warnings.push(entry);
            this.trimArray(this.warnings);
        }
        this.logs.push(entry);
        this.trimArray(this.logs);
    }
    /**
     * Setup global error listeners
     */
    setupErrorListeners() {
        window.addEventListener('error', this.handleError);
        window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
    }
    /**
     * Format console arguments into a string
     */
    formatArgs(args) {
        return args.map(arg => {
            if (typeof arg === 'object') {
                try {
                    return JSON.stringify(arg, null, 2);
                }
                catch (e) {
                    return String(arg);
                }
            }
            return String(arg);
        }).join(' ');
    }
    /**
     * Serialize arguments for storage (handle circular references)
     */
    serializeArgs(args) {
        return args.map(arg => {
            if (typeof arg === 'object' && arg !== null) {
                try {
                    // Handle circular references
                    const seen = new WeakSet();
                    return JSON.parse(JSON.stringify(arg, (_key, value) => {
                        if (typeof value === 'object' && value !== null) {
                            if (seen.has(value)) {
                                return '[Circular]';
                            }
                            seen.add(value);
                        }
                        return value;
                    }));
                }
                catch (e) {
                    return '[Object (could not serialize)]';
                }
            }
            return arg;
        });
    }
    /**
     * Trim array to max entries (keep most recent)
     */
    trimArray(arr) {
        if (arr.length > this.maxEntries) {
            arr.splice(0, arr.length - this.maxEntries);
        }
    }
    /**
     * Get all captured data
     */
    getData() {
        return {
            logs: [...this.logs],
            errors: [...this.errors],
            warnings: [...this.warnings],
            totalLogs: this.logs.length,
            totalErrors: this.errors.length,
            totalWarnings: this.warnings.length
        };
    }
    /**
     * Get only errors
     */
    getErrors() {
        return [...this.errors];
    }
    /**
     * Get only warnings
     */
    getWarnings() {
        return [...this.warnings];
    }
    /**
     * Get recent logs (last N entries)
     */
    getRecentLogs(count = 20) {
        return this.logs.slice(-count);
    }
    /**
     * Clear all captured data
     */
    clear() {
        this.logs = [];
        this.errors = [];
        this.warnings = [];
    }
    /**
     * Get capturing status
     */
    isActive() {
        return this.isCapturing;
    }
}
export default ConsoleCapture;
//# sourceMappingURL=console-capture.js.map